/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `CookieProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface CookieProps {
    /**
     * A string representing the CSS class to be applied to the CookieIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default 'currentColor'
     */
    color1?: string;
    /**
     * A number or string representing the height of the CookieIcon element.
     *
     * @default 24
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Cookie'
     */
    testId?: string;
    /**
     * A number or string representing the height of the CookieIcon element.
     *
     * @default 24
     */
    width?: number | string;
}

/**
 * The `Cookie` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `CookieProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the CookieIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the CookieIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the CookieIcon element.
 * @param props.ref       A React ref object to access the underlying SVG element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const CookieComponent = <Cookie className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Cookie = ({
    className = '',
    color1 = 'currentColor',
    height = 24,
    ref,
    testId = 'Cookie',
    width = 24
}: CookieProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 100 100"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <circle cx="45.6" cy="24.1" fill={color1} r="4" />
        <circle cx="52.3" cy="49.9" fill={color1} r="4" />
        <circle cx="27.4" cy="59.1" fill={color1} r="4" />
        <circle cx="27.4" cy="37.3" fill={color1} r="4" />
        <circle cx="40.6" cy="76.6" fill={color1} r="4" />
        <circle cx="69.5" cy="71.6" fill={color1} r="4" />
        <path d="M48.9 95.5c-24 0-44-18.7-45.5-42.7-.8-13.1 4.2-26 13.6-35.3 9.5-9.3 22.5-14 35.6-13 4.3.4 8.6 1.3 12.6 2.9.7.3 1.2.9 1.3 1.6s-.2 1.4-.7 1.9a6.2 6.2 0 0 0-2.2 4.7c0 1.8.8 3.6 2.2 4.7.4.3.7.9.7 1.4s-.1 1.1-.5 1.5a6.2 6.2 0 0 0-1.6 4.1c0 1.9.9 3.7 2.5 4.9.5.4.8 1 .8 1.6s-.3 1.2-.8 1.6c-1.6 1.2-2.5 3-2.5 4.9 0 3.4 2.7 6.1 6.1 6.1h.2c.9 0 1.7.6 2 1.4.8 2.6 3.2 4.4 5.8 4.4 1.6 0 3.1-.6 4.3-1.8.5-.5 1.3-.7 2-.5s1.2.7 1.4 1.4a6.2 6.2 0 0 0 5.5 4.5c.6 0 1.1.3 1.5.8.3.4.5 1 .4 1.6-3.8 21.6-22.7 37.3-44.7 37.3M49 8.3c-10.8 0-21.3 4.3-29.1 12A41.4 41.4 0 0 0 7.4 52.6a41.65 41.65 0 0 0 41.5 38.9c19.4 0 36.2-13.4 40.5-32.1a10 10 0 0 1-5.8-4.5 10.3 10.3 0 0 1-14.1-4.7 10 10 0 0 1-9-10c0-2.4.8-4.7 2.4-6.5-1.5-1.8-2.4-4.1-2.4-6.5 0-1.8.5-3.6 1.5-5.2-1.5-1.8-2.4-4.1-2.4-6.5 0-1.9.5-3.8 1.5-5.3-2.9-.9-5.8-1.5-8.8-1.7-1.1-.1-2.2-.2-3.3-.2" fill={color1} />
        <path d="M35 30q5 5 10 0m5 30q5 5 10 0M30 50q5 5 10 0m5 20q5 5 10 0" fill="transparent" opacity=".3" stroke={color1} strokeWidth=".8" />
    </svg>
);

Cookie.displayName = 'Cookie';